import { sanitizeBusinessMessage, toUserFacingApiError } from '../utils/userFacingError'
import { api } from './api'

function assertSuccess(body: { success?: boolean; message?: string; data?: unknown }) {
  if (!body?.success) {
    throw new Error(sanitizeBusinessMessage(body?.message, 'Request failed'))
  }
  return body.data
}

function apiError(err: unknown, fallback: string) {
  return toUserFacingApiError(err, fallback)
}

export type CustomerListItem = {
  id: number
  email: string
  firstName: string
  lastName: string
  createdAt: string
  orderCount: number
  totalSpend: string
  currency: string
}

export type CustomerAddress = {
  id: number
  label: string
  name: string
  phone: string
  street: string
  city: string
  state: string
  country: string
  zip: string
  deliveryNotes: string | null
  isDefault: boolean
  store: { id: number; name: string; subdomain: string } | null
}

export type CustomerOrderSummary = {
  id: number
  orderReference: string
  storeId: number
  status: string
  paymentStatus: string
  currency: string
  total: string
  createdAt: string
  store: { id: number; name: string; subdomain: string } | null
}

export type CustomerDetail = {
  id: number
  email: string
  firstName: string
  lastName: string
  createdAt: string
  updatedAt: string
  orderCount: number
  totalSpend: string
  currency: string
  addresses: CustomerAddress[]
  orders: {
    items: CustomerOrderSummary[]
    total: number
    page: number
    limit: number
  }
}

export type CustomersListParams = {
  page?: number
  limit?: number
  q?: string
}

export type CustomersListResponse = {
  items: CustomerListItem[]
  total: number
  page: number
  limit: number
}

function normalizeListItem(raw: Record<string, unknown>): CustomerListItem {
  return {
    id: raw.id as number,
    email: String(raw.email ?? ''),
    firstName: String(raw.firstName ?? ''),
    lastName: String(raw.lastName ?? ''),
    createdAt: String(raw.createdAt ?? ''),
    orderCount: Number(raw.orderCount ?? 0),
    totalSpend: String(raw.totalSpend ?? '0'),
    currency: String(raw.currency ?? ''),
  }
}

export function customerDisplayName(item: Pick<CustomerListItem, 'firstName' | 'lastName' | 'email'>) {
  const name = [item.firstName, item.lastName].filter(Boolean).join(' ').trim()
  return name || item.email || '—'
}

export async function fetchCustomers(params: CustomersListParams = {}): Promise<CustomersListResponse> {
  try {
    const page = params.page ?? 1
    const limit = params.limit ?? 20
    const { data } = await api.get('/storebusiness/customers', {
      params: {
        page,
        limit,
        ...(params.q ? { q: params.q } : {}),
      },
    })
    const payload = assertSuccess(data) as CustomersListResponse
    const rows = (payload.items || []) as Record<string, unknown>[]
    return {
      items: rows.map((r) => normalizeListItem(r)),
      total: payload.total ?? 0,
      page: payload.page ?? page,
      limit: payload.limit ?? limit,
    }
  } catch (e) {
    throw apiError(e, 'Failed to load customers')
  }
}

export async function fetchCustomerById(
  customerId: number,
  params: { page?: number; limit?: number } = {},
): Promise<CustomerDetail> {
  try {
    const page = params.page ?? 1
    const limit = params.limit ?? 10
    const { data } = await api.get(`/storebusiness/customers/${customerId}`, {
      params: { page, limit },
    })
    return assertSuccess(data) as CustomerDetail
  } catch (e) {
    throw apiError(e, 'Failed to load customer')
  }
}
